// Shared calendar/configuration rules; no database, browser or local-time dependencies.
const DEFAULT_CONFIG = {enabled: false, approvalMode: 'once', leadDays: 5, managers: {participants: []}};
const REQUEST_ROLES = ['initiator', 'approver', 'assigner', 'executor', 'observer'];
function managerRequestRoles(rule) {
    const values = rule.roles || rule.request_roles || [rule.role || rule.request_role];
    return Array.isArray(values) ? values.filter(role => REQUEST_ROLES.includes(role)) : [];
}
const clone = value => JSON.parse(JSON.stringify(value));
function fail(message) { const error = new Error(message); error.statusCode = 400; throw error; }
function date(value) {
    if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) fail('Укажите календарную дату.');
    const time = new Date(value + 'T00:00:00Z');
    if (!Number.isFinite(time.getTime()) || time.toISOString().slice(0, 10) !== value) fail('Некорректная дата.');
    return value;
}
function integer(value, min, max, label) {
    const n = Number(value);
    if (!Number.isInteger(n) || n < min || n > max) fail(`${label}: от ${min} до ${max}.`);
    return n;
}
function addDays(value, days) {
    const time = new Date(date(value) + 'T00:00:00Z');
    time.setUTCDate(time.getUTCDate() + days);
    return time.toISOString().slice(0, 10);
}
function config(type) {
    type = type || {};
    const workflow = typeof type.workflow_config === 'string' ? JSON.parse(type.workflow_config) : (type.workflow_config || {});
    const input = workflow.recurrence || {};
    return Object.assign(clone(DEFAULT_CONFIG), input, {managers: input.managers || {participants: []}});
}
function supports(type) {
    const workflow = typeof type.workflow_config === 'string' ? JSON.parse(type.workflow_config) : (type.workflow_config || {});
    return type.category !== 'timesheet_submission_extension' && !workflow.domain
        && !Object.keys(workflow.customUseCases || {}).length;
}
function validateConfig(type) {
    const value = config(type);
    if (typeof value.enabled !== 'boolean') fail('Регулярные заявки: некорректный переключатель.');
    if (!['once', 'each'].includes(value.approvalMode)) fail('Выберите режим согласования регулярных заявок.');
    value.leadDays = integer(value.leadDays, 0, 365, 'Создание заранее, дней');
    if (value.enabled) {
        if (!supports(type)) fail('Регулярность недоступна для системного типа заявки.');
        if (!Array.isArray(value.managers.participants) || !value.managers.participants.length) {
            fail('Укажите участников, управляющих регулярными заявками.');
        }
        if (value.managers.participants.some(rule => rule.type === 'request_role' && !managerRequestRoles(rule).length)) {
            fail('Выберите роль в заявке для управляющего серией.');
        }
        const workflow = typeof type.workflow_config === 'string' ? JSON.parse(type.workflow_config) : (type.workflow_config || {});
        const actions = [workflow.fuelCardAction || workflow.fuel_card_action,
            workflow.mechanicAssetAction || workflow.mechanic_asset_action]
            .concat(workflow.mechanicAssetActions || workflow.mechanic_asset_actions || []);
        if (value.approvalMode === 'once' && actions.some(action => action && action.enabled &&
            ['submitted','in_review','approved','awaiting_executor','in_progress'].includes(action.triggerStatus || action.trigger_status || 'completed'))) {
            fail('Для типа с действиями над справочником при согласовании выберите согласование каждой заявки.');
        }
    }
    return value;
}
function normalizeSchedule(input) {
    if (!input || typeof input !== 'object') fail('Укажите график регулярной заявки.');
    const kind = input.kind;
    if (!['cron', 'weeks', 'months', 'dates'].includes(kind)) fail('Выберите периодичность.');
    const result = {kind, leadDays: integer(input.leadDays == null ? 5 : input.leadDays, 0, 365, 'Создание заранее, дней')};
    if (kind === 'dates') {
        if (!Array.isArray(input.dates) || !input.dates.length || input.dates.length > 2000) fail('Укажите от 1 до 2000 дат.');
        result.dates = Array.from(new Set(input.dates.map(date))).sort();
        result.startDate = result.dates[0];
        result.endDate = result.dates[result.dates.length - 1];
    } else {
        result.startDate = date(input.startDate);
        result.endDate = input.endDate ? date(input.endDate) : null;
        if (result.endDate && result.endDate < result.startDate) fail('Окончание графика раньше его начала.');
        if (kind === 'cron') result.cron = normalizeCalendarCron(input.cron);
        else result.interval = integer(input.interval, 1, 120, 'Интервал');
    }
    return result;
}
// The task editor emits calendar periods, not arbitrary cron expressions.
// Request deadlines are calendar days, so the editor's clock fields are not used.
function normalizeCalendarCron(value) {
    const fields = String(value || '').trim().split(/\s+/);
    if (fields.length !== 5) fail('Выберите период повтора.');
    const list = (value, min, max, label) => {
        if (!/^\d+(,\d+)*$/.test(value)) fail(`Некорректное поле «${label}».`);
        return Array.from(new Set(value.split(',').map(n => integer(n, min, max, label)))).sort((a, b) => a - b).join(',');
    };
    list(fields[0], 0, 59, 'Минуты'); list(fields[1], 0, 23, 'Часы');
    const day = fields[2] === '*' ? '*' : String(integer(fields[2], 1, 31, 'Число месяца'));
    const months = fields[3] === '*' ? '*' : list(fields[3], 1, 12, 'Месяцы');
    const weekdays = fields[4] === '*' ? '*' : list(fields[4], 0, 6, 'Дни недели');
    if ((weekdays !== '*' && (day !== '*' || months !== '*')) || (months !== '*' && day === '*')) fail('Некорректное сочетание периодов повтора.');
    return `0 0 ${day} ${months} ${weekdays}`;
}
function nextCalendarDates(schedule, from, limit) {
    const fields = schedule.cron.split(' ');
    const months = fields[3] === '*' ? null : fields[3].split(',').map(Number);
    const weekdays = fields[4] === '*' ? null : fields[4].split(',').map(Number);
    const dates = [];
    let value = from > schedule.startDate ? from : schedule.startDate;
    while (dates.length < limit && (!schedule.endDate || value <= schedule.endDate)) {
        const day = new Date(value + 'T00:00:00Z');
        const last = new Date(Date.UTC(day.getUTCFullYear(), day.getUTCMonth() + 1, 0)).getUTCDate();
        if ((!months || months.includes(day.getUTCMonth() + 1)) && (!weekdays || weekdays.includes(day.getUTCDay())) &&
            (fields[2] === '*' || day.getUTCDate() === Math.min(Number(fields[2]), last))) dates.push(value);
        if (value === '9999-12-31') break;
        value = addDays(value, 1);
    }
    return dates;
}
function occurrenceDate(schedule, index) {
    if (schedule.kind === 'dates') return schedule.dates[index] || null;
    if (schedule.kind === 'weeks') return addDays(schedule.startDate, index * schedule.interval * 7);
    const start = new Date(schedule.startDate + 'T00:00:00Z');
    const month = start.getUTCMonth() + index * schedule.interval;
    const last = new Date(Date.UTC(start.getUTCFullYear(), month + 1, 0)).getUTCDate();
    return new Date(Date.UTC(start.getUTCFullYear(), month, Math.min(start.getUTCDate(), last))).toISOString().slice(0, 10);
}
function nextDates(input, from, limit = 10) {
    const schedule = normalizeSchedule(input);
    date(from);
    if (schedule.kind === 'cron') return nextCalendarDates(schedule, from, limit);
    let index = 0;
    if (schedule.kind === 'weeks') index = Math.max(0, Math.floor((Date.parse(from) - Date.parse(schedule.startDate)) / 86400000 / (7 * schedule.interval)));
    if (schedule.kind === 'months') {
        const a = new Date(schedule.startDate), b = new Date(from);
        index = Math.max(0, Math.floor(((b.getUTCFullYear() - a.getUTCFullYear()) * 12 + b.getUTCMonth() - a.getUTCMonth()) / schedule.interval));
    }
    if (schedule.kind === 'dates') index = schedule.dates.findIndex(value => value >= from);
    if (index < 0) return [];
    const values = [];
    while (values.length < limit) {
        const value = occurrenceDate(schedule, index++);
        if (!value || (schedule.endDate && value > schedule.endDate)) break;
        if (value >= from) values.push(value);
    }
    return values;
}
function describe(input) {
    const s = normalizeSchedule(input);
    if (s.kind === 'dates') return `По выбранным датам (${s.dates.length})`;
    if (s.kind === 'cron') {
        const fields = s.cron.split(' ');
        const weekdays = ['вс', 'пн', 'вт', 'ср', 'чт', 'пт', 'сб'];
        const months = ['январь', 'февраль', 'март', 'апрель', 'май', 'июнь', 'июль', 'август', 'сентябрь', 'октябрь', 'ноябрь', 'декабрь'];
        const period = fields[4] !== '*' ? `Еженедельно: ${fields[4].split(',').map(n => weekdays[Number(n)]).join(', ')}`
            : fields[3] !== '*' ? `Ежегодно: ${fields[3].split(',').map(n => months[Number(n) - 1]).join(', ')}, число ${fields[2]}`
                : fields[2] !== '*' ? `Ежемесячно, число ${fields[2]}` : 'Ежедневно';
        return `${period}, с ${s.startDate}${s.endDate ? ` по ${s.endDate}` : ', до отмены'}`;
    }
    return `Каждые ${s.interval} ${s.kind === 'weeks' ? 'нед.' : 'мес.'}, с ${s.startDate}${s.endDate ? ` по ${s.endDate}` : ', до отмены'}`;
}
module.exports = {DEFAULT_CONFIG, managerRequestRoles, config, supports, validateConfig, normalizeSchedule, nextDates, addDays, date, describe};
